import React, { useState, useEffect } from 'react';
import { Card, Button } from '../common';
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  Send,
  HelpCircle,
  ExternalLink,
  ShieldAlert,
  Server,
  Key,
  Globe,
} from 'lucide-react';
import { getSetting } from '../../db';
import {
  getLineAlertConfig,
  saveLineAlertConfig,
  sendTestAlert,
  type LineAlertConfig,
  type AlertDispatchMode,
} from '../../services/line';
import { saveAndBroadcastConfig } from '../../services/config/cloudConfigService';
import { EMBEDDED_SYSTEM_CONFIG } from '../../config/embeddedConfig';

interface LineSettingsProps {
  onConfigSaved?: (config: LineAlertConfig) => void;
  className?: string;
}

export const LineSettings: React.FC<LineSettingsProps> = ({
  onConfigSaved,
  className = '',
}) => {
  const [config, setConfig] = useState<LineAlertConfig>({
    enabled: true,
    mode: 'gas_relay',
    webhookUrl: '',
    apiToken: '',
    targetId: '',
    gasRelayUrl: '',
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<string>('');
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [testStatus, setTestStatus] = useState<{
    type: 'success' | 'error';
    message: string;
    timestamp: string;
  } | null>(null);
  const [validationError, setValidationError] = useState<string>('');

  // Load persisted configuration on mount
  useEffect(() => {
    let isMounted = true;
    getLineAlertConfig().then(async (loaded) => {
      if (isMounted) {
        if (!loaded.gasRelayUrl) {
          const storedUrl = (await getSetting<string>('gas_endpoint_url')) || '';
          const storedConfig = await getSetting<{ webAppUrl?: string }>('gas_config');
          const gasUrl = storedConfig?.webAppUrl || storedUrl || EMBEDDED_SYSTEM_CONFIG.gas_endpoint_url;
          if (gasUrl) {
            loaded.gasRelayUrl = gasUrl;
          }
        }
        setConfig(loaded);
        setIsLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // Validate inputs
  const validate = (): boolean => {
    setValidationError('');

    if (config.mode === 'direct_webhook') {
      if (config.webhookUrl && !/^https?:\/\//i.test(config.webhookUrl.trim())) {
        setValidationError('Webhook URL ต้องเริ่มต้นด้วย http:// หรือ https://');
        return false;
      }
    } else if (config.mode === 'gas_relay') {
      if (config.gasRelayUrl && !/^https?:\/\//i.test(config.gasRelayUrl.trim())) {
        setValidationError('GAS Relay URL ต้องเริ่มต้นด้วย https://');
        return false;
      }
      if (!config.apiToken.trim()) {
        setValidationError('จำเป็นต้องระบุ LINE Channel Access Token สำหรับ GAS Relay');
        return false;
      }
    } else if (config.mode === 'line_messaging_api') {
      if (!config.apiToken.trim()) {
        setValidationError('จำเป็นต้องระบุ LINE Channel Access Token');
        return false;
      }
      if (!config.targetId.trim()) {
        setValidationError('จำเป็นต้องระบุ Target User ID หรือ Group ID (เช่น U... หรือ C...)');
        return false;
      }
    } else if (config.mode === 'line_notify') {
      if (!config.apiToken.trim() && !config.webhookUrl.trim()) {
        setValidationError('จำเป็นต้องระบุ LINE Notify Token หรือ Webhook Proxy URL');
        return false;
      }
    }

    return true;
  };

  // Save Settings & Broadcast to all devices via Google Sheets
  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setSaveSuccess('');
    try {
      const broadcastRes = await saveAndBroadcastConfig({
        line_alert_config: config,
      });
      setSaveSuccess(broadcastRes.message);
      if (onConfigSaved) {
        onConfigSaved(config);
      }
      setTimeout(() => setSaveSuccess(''), 5000);
    } catch (err: any) {
      setValidationError(err.message || 'บันทึกการตั้งค่าล้มเหลว');
    } finally {
      setIsSaving(false);
    }
  };

  // Dispatch Test Notification
  const handleTestNotification = async () => {
    if (!validate()) return;

    setIsTesting(true);
    setTestStatus(null);

    try {
      const res = await sendTestAlert({
        mode: config.mode,
        webhookUrl: config.webhookUrl.trim(),
        apiToken: config.apiToken.trim(),
        targetId: config.targetId.trim(),
        gasRelayUrl: config.gasRelayUrl.trim(),
      });

      setTestStatus({
        type: 'success',
        message: res.message || 'ส่งข้อความทดสอบสำเร็จแล้ว! กรุณาตรวจสอบใน LINE หรือระบบ Webhook ปลายทาง',
        timestamp: new Date().toLocaleTimeString(),
      });
    } catch (err: any) {
      setTestStatus({
        type: 'error',
        message: err.message || 'การส่งข้อความทดสอบล้มเหลว ตรวจสอบ URL หรือ Token',
        timestamp: new Date().toLocaleTimeString(),
      });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse p-6">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4" />
        <div className="h-10 bg-slate-200 rounded mb-2" />
        <div className="h-10 bg-slate-200 rounded" />
      </Card>
    );
  }

  return (
    <Card
      title={
        <div className="flex items-center gap-2.5">
          <ShieldAlert className="w-5 h-5 text-rose-500" />
          <span className="text-slate-900 font-bold">
            การแจ้งเตือนความผิดปกติเครื่องจักร (LINE & Webhook Alerting)
          </span>
        </div>
      }
      subtitle="ส่งแจ้งเตือนด่วนไปยังกลุ่มช่างบำรุงรักษาทันทีเมื่อตรวจพบค่า NG หรือแรงดันตกนอกเกณฑ์มาตรฐาน"
      className={className}
    >
      <div className="space-y-5">
        {/* Enable / Disable Alert System Switch */}
        <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-200">
          <div className="space-y-0.5">
            <span className="text-sm font-bold text-slate-800">
              เปิดใช้งานการแจ้งเตือนความผิดปกติ (Alert System)
            </span>
            <p className="text-xs text-slate-500">
              เมื่อเปิดใช้งาน ระบบจะส่งข้อความแจ้งเตือนอัตโนมัติเมื่อตรวจพบข้อบกพร่อง
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className={`w-12 h-7 rounded-full transition-colors duration-200 ease-in-out p-0.5 flex items-center ${config.enabled ? 'bg-emerald-600' : 'bg-slate-300'}`}>
              <div
                className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                  config.enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </div>
          </label>
        </div>

        {/* Mode Selector */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
            รูปแบบการเชื่อมต่อ (Integration Mode)
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              {
                id: 'direct_webhook' as AlertDispatchMode,
                label: 'Direct Webhook',
                desc: 'Make, n8n, Zapier, Discord',
                icon: Globe,
              },
              {
                id: 'gas_relay' as AlertDispatchMode,
                label: 'GAS CORS Relay',
                desc: 'ผ่าน Google Apps Script',
                icon: Server,
              },
              {
                id: 'line_messaging_api' as AlertDispatchMode,
                label: 'LINE Messaging API',
                desc: 'Push Bot ตรง (CORS required)',
                icon: Send,
              },
              {
                id: 'line_notify' as AlertDispatchMode,
                label: 'LINE Notify',
                desc: 'ข้อความข้อความธรรมดา (Token)',
                icon: Bell,
              },
            ].map((m) => {
              const Icon = m.icon;
              const isSelected = config.mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setConfig({ ...config, mode: m.id })}
                  className={`flex flex-col text-left p-3 rounded-xl border transition-all min-h-[56px] select-none ${
                    isSelected
                      ? 'bg-rose-50 border-rose-500 text-rose-800 ring-1 ring-rose-500'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-1.5 font-bold text-xs">
                    <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-rose-600' : 'text-slate-500'}`} />
                    <span>{m.label}</span>
                  </div>
                  <span className="text-[11px] text-slate-500 mt-1">{m.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Input Fields depending on Mode */}
        <div className="space-y-4 pt-1">
          {/* Direct Webhook Mode */}
          {config.mode === 'direct_webhook' && (
            <div>
              <label
                htmlFor="line-webhook-url-input"
                className="block text-xs font-semibold text-slate-700 mb-1.5"
              >
                Webhook URL (Make / n8n / Zapier / Discord / Custom Endpoint) <span className="text-rose-500">*</span>
              </label>
              <input
                id="line-webhook-url-input"
                type="url"
                value={config.webhookUrl}
                onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
                placeholder="https://hook.eu1.make.com/... หรือ https://discord.com/api/webhooks/..."
                className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                ระบบจะส่ง JSON Payload พร้อมทั้งโครงสร้าง LINE Flex Message v2 Container และข้อมูลความผิดปกติครบถ้วน
              </p>
            </div>
          )}

          {/* GAS CORS Relay Mode */}
          {config.mode === 'gas_relay' && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label
                    htmlFor="gas-relay-url-input"
                    className="block text-xs font-semibold text-slate-700"
                  >
                    Google Apps Script Web App URL (Relay Endpoint) <span className="text-rose-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={async () => {
                      const storedUrl = (await getSetting<string>('gas_endpoint_url')) || '';
                      const storedConfig = await getSetting<{ webAppUrl?: string }>('gas_config');
                      const gasUrl = storedConfig?.webAppUrl || storedUrl;
                      if (gasUrl) {
                        setConfig((prev) => ({ ...prev, gasRelayUrl: gasUrl }));
                      }
                    }}
                    className="text-[11px] text-emerald-600 hover:text-emerald-700 underline font-semibold cursor-pointer"
                  >
                    🔗 ดึง URL จาก Google Sheets (แนะนำ)
                  </button>
                </div>
                <input
                  id="gas-relay-url-input"
                  type="url"
                  value={config.gasRelayUrl}
                  onChange={(e) => setConfig({ ...config, gasRelayUrl: e.target.value })}
                  placeholder="https://script.google.com/macros/s/.../exec"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>

              <div>
                <label
                  htmlFor="gas-relay-token-input"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  LINE Channel Access Token <span className="text-rose-500">*</span>
                </label>
                <input
                  id="gas-relay-token-input"
                  type="password"
                  value={config.apiToken}
                  onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                  placeholder="Bearer Token จาก LINE Developers Console..."
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>

              <div>
                <label
                  htmlFor="gas-relay-target-id-input"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Target User ID / Group ID (เช่น U... หรือ C...)
                </label>
                <input
                  id="gas-relay-target-id-input"
                  type="text"
                  value={config.targetId}
                  onChange={(e) => setConfig({ ...config, targetId: e.target.value })}
                  placeholder="U1234567890abcdef... หรือ C1234567890abcdef..."
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>
            </div>
          )}

          {/* LINE Messaging API Direct Mode */}
          {config.mode === 'line_messaging_api' && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="font-bold block text-amber-800">
                    คำแนะนำด้านความปลอดภัย (CORS Policy):
                  </span>
                  <p className="text-amber-800 text-[11px] leading-relaxed">
                    ระบบของ LINE ไม่อนุญาตให้หน้าเว็บเบราว์เซอร์ส่งข้อความหา API ตรงๆ (จะขึ้นข้อผิดพลาด <em>Failed to fetch</em>) หากรันผ่านเบราว์เซอร์ แนะนำให้กดเลือกโหมด <strong>GAS CORS Relay</strong> ด้านบน เพื่อส่งผ่าน Google Apps Script แทนครับ
                  </p>
                  <button
                    type="button"
                    onClick={() => setConfig({ ...config, mode: 'gas_relay' })}
                    className="mt-1 text-[11px] font-bold text-amber-700 underline hover:text-amber-800 cursor-pointer"
                  >
                    👉 คลิกที่นี่เพื่อเปลี่ยนเป็นโหมด GAS CORS Relay ทันที
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="line-api-token-input"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  LINE Channel Access Token (Long-Lived) <span className="text-rose-500">*</span>
                </label>
                <input
                  id="line-api-token-input"
                  type="password"
                  value={config.apiToken}
                  onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                  placeholder="Token จาก LINE Developers Console..."
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>

              <div>
                <label
                  htmlFor="line-api-target-id-input"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Target User ID / Group ID <span className="text-rose-500">*</span>
                </label>
                <input
                  id="line-api-target-id-input"
                  type="text"
                  value={config.targetId}
                  onChange={(e) => setConfig({ ...config, targetId: e.target.value })}
                  placeholder="U1234567890... หรือ C1234567890..."
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>

              <div>
                <label
                  htmlFor="line-api-custom-url-input"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  Custom Endpoint URL (Optional Proxy)
                </label>
                <input
                  id="line-api-custom-url-input"
                  type="url"
                  value={config.webhookUrl}
                  onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
                  placeholder="ค่าเริ่มต้น: https://api.line.me/v2/bot/message/push"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>
            </div>
          )}

          {/* LINE Notify Mode */}
          {config.mode === 'line_notify' && (
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="line-notify-token-input"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  LINE Notify Personal Access Token <span className="text-rose-500">*</span>
                </label>
                <input
                  id="line-notify-token-input"
                  type="password"
                  value={config.apiToken}
                  onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                  placeholder="Token จาก https://notify-bot.line.me/my/"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>

              <div>
                <label
                  htmlFor="line-notify-proxy-url-input"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  LINE Notify Proxy URL (Optional for CORS bypass)
                </label>
                <input
                  id="line-notify-proxy-url-input"
                  type="url"
                  value={config.webhookUrl}
                  onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
                  placeholder="ค่าเริ่มต้น: https://notify-api.line.me/api/notify"
                  className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:ring-2 focus:ring-rose-500 focus:border-rose-500 focus:outline-none min-h-[48px] placeholder-slate-400"
                />
              </div>
            </div>
          )}
        </div>

        {/* Validation Error Message */}
        {validationError && (
          <div className="bg-rose-50 border border-rose-300 rounded-xl p-3 text-xs text-rose-800 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        {/* Test Result Message */}
        {testStatus && (
          <div
            className={`p-3.5 rounded-xl border flex items-start gap-2.5 text-xs animate-in fade-in duration-150 ${
              testStatus.type === 'success'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                : 'bg-rose-50 border-rose-300 text-rose-800'
            }`}
          >
            {testStatus.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            )}
            <div className="space-y-0.5">
              <span className="font-bold">
                {testStatus.type === 'success' ? 'ทดสอบการส่งสำเร็จ' : 'ทดสอบการส่งล้มเหลว'} ({testStatus.timestamp}):
              </span>
              <p>{testStatus.message}</p>
            </div>
          </div>
        )}

        {/* Action Buttons: Test Notification & Save */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-slate-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleTestNotification}
            isLoading={isTesting}
            disabled={!config.enabled}
            leftIcon={<Send className="w-4 h-4 text-rose-600" />}
            className="w-full sm:w-auto"
          >
            {isTesting ? 'กำลังส่งแจ้งเตือนทดสอบ...' : 'ทดสอบส่งการแจ้งเตือน (Test Notification)'}
          </Button>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            {saveSuccess && (
              <span className="text-xs font-bold text-emerald-600 animate-in fade-in">
                {saveSuccess}
              </span>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              isLoading={isSaving}
              leftIcon={<CheckCircle2 className="w-4 h-4" />}
              className="w-full sm:w-auto"
            >
              บันทึกและกระจายไปยังทุกเครื่อง
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
